import i18n from 'i18next';
import detector from 'i18next-browser-languagedetector';
import backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

const BUILD = 'local';
const DEFAULT_LANGUAGE = localStorage.getItem('lng') || window?.etesting?.language || navigator.language;

// `baseDir` is the i18n directory (e.g. '/v2/i18n'); `ns` lists the namespaces to load,
// ordered [common, <domain>]. The domain namespace is the default, with `common` as fallback,
// so shared component/infra keys resolve in both App and BO.
export const init = (baseDir = '/v2/i18n', ns = ['common', 'app']) =>
    i18n
        .use(backend)
        .use(detector)
        .use(initReactI18next)
        .init({
            compatibilityJSON: 'v4',
            lng: DEFAULT_LANGUAGE,
            fallbackLng: ['fr'],
            ns,
            defaultNS: ns[ns.length - 1],
            fallbackNS: 'common',
            keySeparator: false,
            nsSeparator: false,
            interpolation: {
                escapeValue: false,
            },
            backend: {
                loadPath: `${baseDir}/{{ns}}/{{lng}}.json?${BUILD}`,
            },
            react: {
                useSuspense: false,
            },
            detection: {
                order: ['localStorage', 'navigator'],
                lookupLocalStorage: 'lng',
                lookupFromPathIndex: 0,
                lookupFromSubdomainIndex: 0,
                caches: ['localStorage'],
                checkWhitelist: true,
                checkForSimilarInWhitelist: false,
            },
            supportedLngs: ['en', 'fr', 'de', 'it', 'es', 'pl', 'nl', 'pt', 'ro', 'ua'],
        });

export default i18n;

export const getTranslatedString = (translatedValue = [], preferredLanguage = null) => {
    if (!Array.isArray(translatedValue)) {
        return null;
    }
    const preferred = (preferredLanguage ? translatedValue.find((e) => e.language === preferredLanguage)?.value : null) || null;
    const current = translatedValue.find((e) => e.language === DEFAULT_LANGUAGE)?.value || null;
    const fallbackFr = translatedValue.find((e) => e.language === 'en')?.value || null;
    const fallbackEn = translatedValue.find((e) => e.language === 'fr')?.value || null;
    const fallback = translatedValue?.[0]?.value || null;
    return preferred || current || fallbackFr || fallbackEn || fallback || null;
};

// Reference language of the JSON i18n texts ({"fr": "...", "en": "..."}): a plain non-JSON string is read
// as this language, and it is the first choice for display.
export const REFERENCE_LANGUAGE = 'fr';

// Parses a JSON i18n string ({"fr": "...", "en": "..."}) into an object keyed by language; a plain
// non-JSON string is read as the reference language.
export const parseI18n = (value) => {
    if (!value) return {};
    if (typeof value !== 'string') return value;
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : { [REFERENCE_LANGUAGE]: value };
    } catch {
        return { [REFERENCE_LANGUAGE]: value };
    }
};

// Keeps the filled languages of a JSON i18n object, trimmed.
export const compactI18n = (texts = {}) =>
    Object.fromEntries(
        Object.entries(texts)
            .map(([language, text]) => [language, (text ?? '').trim()])
            .filter(([, text]) => text !== ''),
    );

// Text to display from a JSON i18n object: the reference language, else the first filled one.
export const displayText = (texts = {}) => texts[REFERENCE_LANGUAGE] || Object.values(texts).find((text) => text) || '';
